import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Sparkles, MapPin } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1758270705172-07b53627dfcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxzdHVkZW50cyUyMGxlYXJuaW5nJTIwY29kaW5nJTIwbW9kZXJuJTIwdGVjaCUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3ODkyMTkyNDV8MA&ixlib=rb-4.1.0&q=85";

const lineVariants = {
  hidden: {},
  show: (i) => ({ transition: { staggerChildren: 0.045, delayChildren: 0.25 + i * 0.22 } }),
};
const wordVariants = {
  hidden: { y: "115%", rotate: 3 },
  show: { y: "0%", rotate: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const KineticLine = ({ text, index, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span className="block" variants={lineVariants} custom={index} initial="hidden" animate="show">
      {text.split(" ").map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.24em]">
          <motion.span variants={wordVariants} className={`inline-block ${accent && i === text.split(" ").length - 1 ? "text-[#C85A17]" : ""}`}>
            {w}
          </motion.span>
        </span>
      ))}
    </motion.span>
  </span>
);

export const KineticHero = ({ onOpenChat }) => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 8);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };
  const resetTilt = () => { rx.set(0); ry.set(0); };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={sectionRef} className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{ backgroundImage: "radial-gradient(#E7DFD5 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xs font-mono uppercase tracking-[0.22em] text-[#C85A17] font-semibold mb-6" data-testid="hero-overline">
            {t.hero.overline}
          </motion.p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-[#17181A]" data-testid="hero-heading">
            {t.hero.lines.map((line, i) => (
              <KineticLine key={`${line}-${i}`} text={line} index={i} accent={i === t.hero.lines.length - 1} />
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#5F6368] max-w-xl" data-testid="hero-subtitle">
            {t.hero.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-4">
            <button data-testid="hero-primary-cta-button" onClick={() => scrollTo("enroll")}
              className="group inline-flex items-center gap-2 bg-[#163832] text-[#FDFCF7] font-semibold rounded-full px-7 py-3.5 hover:bg-[#0f2a25] transition-colors duration-200">
              {t.hero.ctaPrimary}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-ai-advisor-button" onClick={onOpenChat}
              className="inline-flex items-center gap-2 border border-[#17181A]/20 text-[#17181A] font-semibold rounded-full px-7 py-3.5 hover:border-[#C85A17] hover:text-[#C85A17] transition-colors duration-200 bg-white/50">
              <Sparkles className="w-4 h-4 text-[#C85A17]" />
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap gap-x-10 gap-y-4" data-testid="hero-stats-badge">
            {t.hero.stats.map((s, i) => (
              <div key={i}>
                <div className="font-display text-2xl sm:text-3xl font-bold text-[#17181A]">{s.value}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#5F6368] mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5" style={{ perspective: 1000 }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
            onMouseMove={onTilt} onMouseLeave={resetTilt}
            className="relative rounded-2xl border border-[#E7DFD5] bg-white p-2.5 shadow-[0_24px_60px_-24px_rgba(23,24,26,0.25)]" data-testid="hero-spotlight-frame">
            <div className="relative overflow-hidden rounded-xl aspect-[4/5]">
              <motion.img src={HERO_IMG} alt="Students collaborating in a modern tech lab" style={{ y: imgY }} loading="eager"
                className="absolute inset-0 w-full h-[120%] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121516]/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="text-[#FDFCF7] text-xs font-mono tracking-wider flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#C85A17]" />{t.hero.imageCaption}
                </span>
              </div>
            </div>
            <div className="absolute -top-3.5 -right-3.5 bg-[#C85A17] text-[#FDFCF7] text-xs font-semibold rounded-full px-4 py-2 shadow-lg rotate-3" data-testid="hero-cohort-badge">
              {t.hero.cohortBadge}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
